// variant-b.jsx — Cards. Paged horizontal favorites hero + stacked rounded cards.
// Ported from /tmp/handoff-fcc-weather/src/variants-ab.jsx::VariantB.

import { useEffect, useRef, useState } from 'preact/hooks';
import { WxIcon, Glyph } from '../icons.jsx';
import { HeroMedia } from '../hero/hero-media.jsx';
import { PullToRefresh, DayRow, HourCell, MetricChip, Wordmark, AlertBanner, AuthFooter } from '../atoms.jsx';
import { SunArc } from './shared.jsx';
import {
  getCurrentForState, getHourly, getTenDay, getMetrics,
  getAlert, hasActiveAlert, getLocation, getUpdatedLabel, getSun, getFavorites,
  refresh,
} from '../data-bridge.js';

export function VariantB({ brand, density, state, sheetOpen, setSheetOpen, tipFn, heroTreatment = 'subtle', bucket = 'midday', brandKey = 'shared' }) {
  const s = getCurrentForState(state);
  const hourly = getHourly(state);
  const tenDay = getTenDay(state);
  const metrics = getMetrics(state);
  const sun = getSun();
  const location = getLocation();
  const updated = getUpdatedLabel();
  const favorites = getFavorites();
  const max = Math.max(...tenDay.map((d) => d.hi));
  const min = Math.min(...tenDay.map((d) => d.lo));
  const [expanded, setExpanded] = useState(null);
  const [favIdx, setFavIdx] = useState(0);
  const showAlert = hasActiveAlert() || state === 'storm' || state === 'rain';
  const alert = getAlert();

  const sliderRef = useRef(null);
  useEffect(() => {
    sliderRef.current?.scrollTo({ left: favIdx * sliderRef.current.clientWidth, behavior: 'smooth' });
  }, [favIdx]);

  const card = (children, extra = {}) => (
    <div style={{
      background: brand.panel, borderRadius: 18, border: `1px solid ${brand.line}`,
      margin: `0 ${density.pad}px ${density.gap}px`,
      overflow: 'hidden', ...extra,
    }}>{children}</div>
  );

  // Always show the current location as the first card; then real favorites.
  const cards = [
    { id: 'now', label: location.label.split(',')[0] || location.label, region: (location.label.includes(',') ? location.label.split(',').slice(1).join(',').trim() : ''), local: s },
    ...favorites.filter((f) => f.id !== 'active').map((f) => ({ id: f.id, label: f.label, region: f.region || '', local: { temp: f.temp || s.temp, feels: (f.temp || s.temp) - 2, cond: f.cond || s.cond, icon: f.icon || s.icon } })),
  ];

  return (
    <PullToRefresh brand={brand} onRefresh={refresh}>
      <div style={{ paddingTop: 72, paddingBottom: 60 }}>
        {/* top row: brand + search */}
        <div style={{ padding: `0 ${density.pad}px ${density.gap}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Wordmark brand={brand} size={14}/>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setSheetOpen?.(true)} className="wx-tap" aria-label="Search" style={{
              width: 36, height: 36, borderRadius: 18, background: brand.panel, border: `1px solid ${brand.line}`,
              color: brand.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}><Glyph.Search s={16}/></button>
            <button onClick={() => setSheetOpen?.(true)} className="wx-tap" aria-label="Add favorite" style={{
              width: 36, height: 36, borderRadius: 18, background: brand.panel, border: `1px solid ${brand.line}`,
              color: brand.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}><Glyph.Plus s={16}/></button>
          </div>
        </div>

        {/* PAGED favorites hero */}
        <div ref={sliderRef} style={{
          display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none', marginBottom: density.gap,
        }} className="wx-hide-scroll" onScroll={(e) => {
          const i = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth);
          if (i !== favIdx) setFavIdx(i);
        }}>
          {cards.map((c) => (
            <div key={c.id} style={{ flexShrink: 0, width: '100%', scrollSnapAlign: 'start', padding: `0 ${density.pad}px` }}>
              <div style={{
                position: 'relative', borderRadius: 22, overflow: 'hidden',
                background: brand.panel, border: `1px solid ${brand.line}`,
                height: 260,
              }}>
                <HeroMedia state={c.local.icon === 'partly-cloudy' ? 'cloudy' : c.local.icon} brand={brand}
                           treatment="subtle" motion={heroTreatment !== 'poster'} creditAt="top"
                           bucket={bucket} brandKey={brandKey}
                           style={{ opacity: .9 }}/>
                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, transparent 30%, ${brand.panel}f0 100%)` }}/>
                <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '16px 18px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Glyph.Pin s={14} c="#fff"/>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', letterSpacing: '-.01em' }}>{c.label}{c.region ? `, ${c.region}` : ''}</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', marginTop: 2 }}>{updated}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 72, fontWeight: 200, color: '#fff', lineHeight: 1, letterSpacing: '-.04em', fontVariantNumeric: 'tabular-nums' }}>{c.local.temp}°</div>
                      <div style={{ fontSize: 13, color: '#fff', opacity: .9, marginTop: 2 }}>{c.local.cond}</div>
                    </div>
                    <WxIcon kind={c.local.icon} size={64}/>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* page dots */}
        <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: density.gap }}>
          {cards.map((_, i) => (
            <div key={i} style={{
              width: i === favIdx ? 18 : 5, height: 5, borderRadius: 3,
              background: i === favIdx ? brand.accent : brand.border,
              transition: 'width .25s, background .25s',
            }}/>
          ))}
        </div>

        {showAlert && <AlertBanner alert={alert} brand={brand} density={density} compact={false}/>}

        {card(
          <>
            <div style={{ padding: `${density.gap}px ${density.pad}px ${density.gap-2}px`, fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', display: 'flex', justifyContent: 'space-between' }}>
              <span>Next 12 hours</span>
              <span style={{ color: brand.accent }}>H {s.hi}° / L {s.lo}°</span>
            </div>
            <div style={{ display: 'flex', overflowX: 'auto' }} className="wx-hide-scroll">
              {hourly.map((hr, i) => <HourCell key={i} hr={hr} brand={brand} density={density} peak={i === 0}/>)}
            </div>
          </>
        )}

        {card(
          <>
            <div style={{ padding: `${density.gap}px ${density.pad}px ${density.gap-4}px`, fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>10-day forecast</div>
            {tenDay.map((row, i) => <DayRow key={i} row={row} idx={i} max={max} min={min} brand={brand} density={density} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)}/>)}
          </>
        )}

        {card(
          <div style={{ padding: density.pad, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {metrics.map((m) => <MetricChip key={m.k} m={m} brand={{ ...brand, panel: 'transparent', line: brand.border }} density={density} onLongPress={() => tipFn?.({ tip: m.why, anchor: m.k })}/>)}
          </div>
        )}

        {card(
          <div style={{ padding: density.pad }}>
            <div style={{ fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Sun</div>
            <SunArc brand={brand} rise={sun.rise} set={sun.set} dayLen={sun.dayLen}/>
          </div>
        )}

        <AuthFooter brand={brand} density={density}/>
      </div>
    </PullToRefresh>
  );
}
